'use client';

import React, { useState } from "react";
import {
    Box, Typography, Divider, ListItem, ListItemText,
    Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { gtmClickTracking } from "@/utils";
import cssStyles from '../../[vendor]/product/product.module.scss';

interface AboutProductIslandProps {
    productId: string;
    productName: string;
    productDetails: string | any;
    vendorDisplayName: string;
    productDimensions?: any;
    productCategorization?: string;
    assemblyInstructions?: string;
    productFeatures?: string[];
}

/**
 * Client island for about-product section with expandable accordions.
 * Needs useState for accordion expand/collapse and show-more toggle.
 */
const AboutProductIsland: React.FC<AboutProductIslandProps> = ({
    productId,
    productName,
    productDetails: productDesc,
    vendorDisplayName,
    productDimensions,
    productCategorization,
    assemblyInstructions,
    productFeatures,
}) => {
    const [expanded, setExpanded] = useState<string | false>(false);
    const [showMoreProductInfo, setShowMoreProductInfo] = useState(false);

    const productCategorizationData: any = productCategorization ? JSON.parse(productCategorization) : null;
    let productCategorizationIndex = 0;

    const handleChange = (panel: string, sectionName: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
        if (isExpanded) {
            gtmClickTracking({
                event: 'Product_Details_Clicked',
                section: sectionName,
                product_id: productId,
                product_name: productName,
            });
        }
    };

    return (
        <div>
            <Divider sx={{ mt: '20px', mb: '20px' }} />
            <Box component="div" className={cssStyles["about-product"]}>
                <h2>About the Product</h2>
                <Box component="div" sx={{ display: 'flex', marginBottom: '10px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Brand</Typography>: {vendorDisplayName}
                </Box>
                <Box component="div" className={!showMoreProductInfo ? cssStyles.product_desc_collapsed : ''}>
                    {typeof productDesc === 'object' ? (productDesc as any)?.romanceCopy : productDesc}
                </Box>
                <Box component="div" className={cssStyles.show_more}>
                    <a href="javascript:void(0)" onClick={() => setShowMoreProductInfo(!showMoreProductInfo)}>
                        {!showMoreProductInfo ? "More +" : "Less -"}
                    </a>
                </Box>
                <Box component="div" sx={{ mb: '20px' }} />
                <Box component="div" className={cssStyles["additional-info"]}>
                    {productDimensions !== null && productDimensions !== undefined && (
                        <Accordion className={cssStyles["additional-info__accordion"]}
                            key={'productDimentions'} expanded={expanded === 'panel-productDimentions'}
                            onChange={handleChange('panel-productDimentions', 'Product Dimensions')} disableGutters>
                            <AccordionSummary expandIcon={expanded === 'panel-productDimentions' ? <RemoveIcon /> : <AddIcon />}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Product Dimensions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ul>
                                    <ListItem key='productHeight' component="li">
                                        <ListItemText><Typography variant="body2">Height: {productDimensions['height']} inches</Typography></ListItemText>
                                    </ListItem>
                                    <ListItem key='productWidth' component="li">
                                        <ListItemText><Typography variant="body2">Width: {productDimensions['width']} inches</Typography></ListItemText>
                                    </ListItem>
                                    <ListItem key='productDepth' component="li">
                                        <ListItemText><Typography variant="body2">Depth: {productDimensions['depth']} inches</Typography></ListItemText>
                                    </ListItem>
                                    <ListItem key='productWeight' component="li">
                                        <ListItemText><Typography variant="body2">Weight: {productDimensions['weight']} lbs</Typography></ListItemText>
                                    </ListItem>
                                </ul>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {productCategorizationData !== null && (
                        Object.keys(productCategorizationData).map((key: any) => {
                            productCategorizationIndex += 1;
                            return (
                                <Accordion className={cssStyles["additional-info__accordion"]} key={productCategorizationIndex}
                                    expanded={expanded === `panel${productCategorizationIndex}`}
                                    onChange={handleChange(`panel${productCategorizationIndex}`, key)} disableGutters>
                                    <AccordionSummary expandIcon={expanded === `panel${productCategorizationIndex}` ? <RemoveIcon /> : <AddIcon />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{key}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {productCategorizationData[key] && (
                                            <ul>
                                                {productCategorizationData[key].map((list: any, index: any) => (
                                                    <ListItem key={index} component="li">
                                                        <ListItemText><Typography variant="body2">{list}</Typography></ListItemText>
                                                    </ListItem>
                                                ))}
                                            </ul>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                    )}

                    {assemblyInstructions !== undefined && (
                        <Accordion className={cssStyles["additional-info__accordion"]}
                            key={'assemblyInstructions'} expanded={expanded === 'panel-assemblyInstructions'}
                            onChange={handleChange('panel-assemblyInstructions', 'Assembly Instructions')} disableGutters>
                            <AccordionSummary expandIcon={expanded === 'panel-assemblyInstructions' ? <RemoveIcon /> : <AddIcon />}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Assembly Instructions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2">
                                    {assemblyInstructions && (
                                        <a href={assemblyInstructions} target="_blank">{assemblyInstructions}</a>
                                    )}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    <Accordion className={cssStyles["additional-info__accordion"]}
                        key={'returnPolicy'} expanded={expanded === 'panel-returnPolicy'}
                        onChange={handleChange('panel-returnPolicy', 'Return Policy')} disableGutters>
                        <AccordionSummary expandIcon={expanded === 'panel-returnPolicy' ? <RemoveIcon /> : <AddIcon />}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Return Policy</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2">
                                <a href="https://nestingale.com/pages/return-policy">https://nestingale.com/pages/return-policy</a>
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Box>
        </div>
    );
};

export default AboutProductIsland;
